import { format } from "d3-format";
import { formatWithTimezone, formatCustomDateWithTimezone } from "../../lib/utils/timezoneFormatter";
import { DEFAULT_PANES, } from "../../lib/core";
import { getSeries } from "../../lib/core/registry/SeriesRegistry";
import { resolveSeriesStructuredValue, resolveSeriesValue } from "../../lib/core/seriesValueResolver";
import { asTimeValue, clampIndex, findNearestDateIndex } from "../visibleRangeBridge";
import { formatPrice, formatVolume } from "./numberFormat";
export const priceFormat = format(",.2f");
// V-02: compactPriceFormat removed — number SSOT lives in ./numberFormat
// (formatPrice/formatVolume). priceFormat/volumeFormat stay for frozen callers.
export const volumeFormat = format(".3s");
export const dateFormat = (d, p = "yyyy-mm-dd", timeFormat = "24h") => formatCustomDateWithTimezone(d, p, true, timeFormat);
export const intradayAxisFormat = (d, timeFormat = "24h") => formatWithTimezone(d, { hour: "2-digit", minute: "2-digit", hour12: timeFormat === "12h" });
export const mixedAxisFormat = (d, p) => formatWithTimezone(d, p === "mm/dd/yyyy" ? { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false } : { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
export const dailyAxisFormat = (d, p = "yyyy-mm-dd") => formatCustomDateWithTimezone(d, p, false);
export const CHART_MARGIN = { left: 60, right: 68, top: 8, bottom: 28 };
export const HISTORY_BACKFILL_PAGE_LIMIT = 5000;
export const HISTORY_RECONCILE_MAX_PAGES = 12;
export const HISTORY_MIN_RESIDENT_BARS = 240;
export const FORK_DEGRADE_PIXEL_CHECK_DELAY_MS = 900;
export const FORK_DEGRADE_PIXEL_RECHECK_DELAY_MS = 350;
export const FORK_DEGRADE_REQUIRED_BLANK_CHECKS = 2;
export function isWidgetTestEnvironment() {
    const globalScope = globalThis;
    return typeof globalScope.vi !== "undefined"
        || typeof globalScope.describe !== "undefined"
        || globalScope.process?.env?.NODE_ENV === "test";
}
export function isPrimaryPriceSeries(type) {
    return type === "Candlestick"
        || type === "HollowCandle"
        || type === "OHLC"
        || type === "HeikinAshi"
        || type === "Line"
        || type === "Area";
}
export function resolveTooltipSeriesColor(series, fallback) {
    if (typeof series.color === "string" && series.color.length > 0) {
        return series.color;
    }
    const paramColor = series.params?.["color"];
    if (typeof paramColor === "string" && paramColor.length > 0) {
        return paramColor;
    }
    switch (series.type) {
        case "EMA":
        case "MA":
            return "#2d9cdb";
        case "BollingerBand":
        case "BollingerBands":
            return "#38bdf8";
        case "MACD":
            return "#2962ff";
        case "RSI":
            return "#ff7f0e";
        case "CVDApprox":
        case "CVDRealtime":
            return "#4f46e5";
        case "SMFI":
            return "#06b6d4";
        case "WyckoffWave":
            return "#f59e0b";
        case "KDJ":
            return "#7c3aed";
        case "CCI":
            return "#e11d48";
        default:
            return fallback;
    }
}
export function formatTooltipValue(value, formatter) {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        return "n/a";
    }
    return formatter ? formatter(value) : priceFormat(value);
}
export function buildForkTooltipTokens(pane, datum, textColor) {
    const tokens = [];
    if (pane.tooltip === "ohlc") {
        tokens.push({ key: `${pane.id}-open`, label: "O", value: formatPrice(datum.open), color: textColor }, { key: `${pane.id}-high`, label: "H", value: formatPrice(datum.high), color: textColor }, { key: `${pane.id}-low`, label: "L", value: formatPrice(datum.low), color: textColor }, { key: `${pane.id}-close`, label: "C", value: formatPrice(datum.close), color: textColor }, { key: `${pane.id}-volume`, label: "Vol", value: formatVolume(datum.volume), color: textColor });
    }
    for (let seriesIndex = 0; seriesIndex < pane.series.length; seriesIndex += 1) {
        const series = pane.series[seriesIndex];
        if (series.visible === false) {
            continue;
        }
        if (pane.tooltip === "ohlc" && isPrimaryPriceSeries(series.type)) {
            continue;
        }
        const tooltipEntry = getSeries(series.type).tooltipEntry(series);
        const label = series.overlaySymbolId ?? tooltipEntry.label;
        tokens.push({
            key: `${pane.id}-${series.id ?? `${series.type}-${seriesIndex}`}`,
            label,
            value: formatTooltipValue(resolveSeriesValue(datum, series), tooltipEntry.format),
            color: resolveTooltipSeriesColor(series, tooltipEntry.color ?? textColor),
        });
    }
    return tokens;
}
export function resolveDiscontinuousXExtents(extents, data, xAccessor) {
    if (data.length === 0) {
        return undefined;
    }
    const lastIndex = data.length - 1;
    const defaultStart = xAccessor(data[0]);
    const defaultEnd = xAccessor(data[lastIndex]);
    if (!extents) {
        return [defaultStart, defaultEnd];
    }
    const startIndex = findNearestDateIndex(data, asTimeValue(extents[0]));
    const endTime = asTimeValue(extents[1]);
    const lastBarTime = data[lastIndex].date.getTime();
    if (endTime <= lastBarTime) {
        const endIndex = findNearestDateIndex(data, endTime);
        return [xAccessor(data[startIndex]), xAccessor(data[endIndex])];
    }
    // End extent is in the future → compute virtual index past data edge
    // so the xScale domain extends to wall-clock time, showing blank space
    // between last bar and "now".
    const gapMs = endTime - lastBarTime;
    const avgTimeframeMs = data.length > 1
        ? lastBarTime - data[lastIndex - 1].date.getTime()
        : 86_400_000;
    const gapBars = Math.ceil(gapMs / avgTimeframeMs);
    const maxBars = 100;
    const virtualGap = Math.min(gapBars, maxBars);
    const lastX = xAccessor(data[lastIndex]);
    return [xAccessor(data[startIndex]), lastX + virtualGap];
}
export function resolveDateDomainFromDiscontinuous(domain, data, xAccessor) {
    if (data.length === 0) {
        const epoch = new Date(0);
        return [epoch, epoch];
    }
    const firstIndexValue = xAccessor(data[0]);
    const lastDataIndex = data.length - 1;
    const resolveDate = (value, fallbackIndex) => {
        if (typeof value !== "number" || !Number.isFinite(value)) {
            return data[fallbackIndex].date;
        }
        const dataIndex = clampIndex(Math.round(value - firstIndexValue), data.length);
        return data[dataIndex]?.date ?? data[fallbackIndex].date;
    };
    return [resolveDate(domain[0], 0), resolveDate(domain[1], lastDataIndex)];
}
export function resolveEngineSeriesValueForRuntime(datum, series) {
    switch (series.type) {
        case "MACD": {
            const value = resolveSeriesStructuredValue(datum, series);
            return value?.divergence;
        }
        case "Whale": {
            const value = resolveSeriesStructuredValue(datum, series);
            const buy = value?.whaleBuyVol ?? 0;
            const sell = value?.whaleSellVol ?? 0;
            return buy - sell;
        }
        default:
            return resolveSeriesValue(datum, series);
    }
}
export function buildLegacyPaneSnapshots(panes, heights, canvasSize, scaleSlotDomains) {
    const plotWidth = Math.max(0, canvasSize.width - CHART_MARGIN.left - CHART_MARGIN.right);
    let top = CHART_MARGIN.top;
    return panes.map((pane, index) => {
        const height = heights[index] ?? 0;
        const hasLeftAxis = pane.series.some((series) => series.visible !== false && series.yAxis === "left");
        const hasRightAxis = pane.series.some((series) => series.visible !== false && (series.yAxis === "right" || series.yAxis == null));
        const snapshotTop = top;
        top += height;
        const leftBounds = hasLeftAxis
            ? {
                top: snapshotTop,
                left: 0,
                width: CHART_MARGIN.left,
                height,
            }
            : undefined;
        const rightBounds = hasRightAxis
            ? {
                top: snapshotTop,
                left: canvasSize.width - CHART_MARGIN.right,
                width: CHART_MARGIN.right,
                height,
            }
            : undefined;
        const scaleSlotBounds = {
            ...(leftBounds ? { [`${pane.id}-left`]: leftBounds } : {}),
            ...(rightBounds ? { [`${pane.id}-right`]: rightBounds } : {}),
        };
        const scaleSlotStates = {
            ...(leftBounds ? { [`${pane.id}-left`]: { side: "left", mode: "auto", visible: true, domain: scaleSlotDomains[`${pane.id}-left`] } } : {}),
            ...(rightBounds ? { [`${pane.id}-right`]: { side: "right", mode: "auto", visible: true, domain: scaleSlotDomains[`${pane.id}-right`] } } : {}),
        };
        return {
            paneId: pane.id,
            bounds: {
                top: snapshotTop,
                left: 0,
                width: canvasSize.width,
                height,
            },
            plotBounds: {
                top: snapshotTop,
                left: CHART_MARGIN.left,
                width: plotWidth,
                height,
            },
            axisBounds: {
                ...(leftBounds
                    ? {
                        left: leftBounds,
                    }
                    : {}),
                ...(rightBounds
                    ? {
                        right: rightBounds,
                    }
                    : {}),
            },
            scaleSlotBounds,
            scaleSlotStates,
            headerAnchor: {
                top: snapshotTop,
                left: CHART_MARGIN.left,
                width: plotWidth,
            },
            labelAnchor: {
                top: snapshotTop,
                left: 0,
                height,
            },
        };
    });
}
export function normalizePaneDescriptorsForExplicitScaleSlots(panes) {
    return panes.map((pane) => {
        const normalizedSeries = pane.series.map((series) => ({
            ...series,
            params: series.params ? { ...series.params } : undefined,
            scaleSlotId: `${pane.id}-${series.yAxis}`,
            lineWidth: typeof series.lineWidth === "number" && Number.isFinite(series.lineWidth) ? series.lineWidth : undefined,
            subColors: series.subColors ? { ...series.subColors } : undefined,
        }));
        const activeSeries = normalizedSeries.filter((series) => series.visible !== false);
        const hasLeftAxis = activeSeries.some((series) => series.yAxis === "left");
        const hasRightAxis = activeSeries.some((series) => series.yAxis === "right");
        return {
            ...pane,
            series: normalizedSeries,
            splitScale: hasLeftAxis && hasRightAxis,
        };
    });
}
export const FALLBACK_ADAPTER = {
    fetchBarsInRange: () => Promise.resolve([]),
    fetchBarsBefore: () => Promise.resolve([]),
    subscribeToBar: () => () => { },
    subscribeToTrades: () => () => { },
    subscribeToOrderbook: () => () => { },
    searchSymbols: () => Promise.resolve([]),
    getSourceSemantics: () => ({
        sourceId: "fallback",
        sourceContractVersion: "1.0.0",
        instrumentClass: "custom",
        coverageMode: "finite-snapshot",
        sessionModel: "continuous",
        timezone: "UTC",
        supportsExactRangeFetch: false,
        supportsBackwardAnchor: false,
        supportsForwardAnchor: false,
        supportsLiveBars: false,
        finalityMode: "immutable",
        expectedGapReasons: [],
    }),
};
/**
 * Wrap a StockDataAdapter to ensure correct source semantics for backfill.
 *
 * Problem: adapters that declare `coverageMode: "finite-snapshot"` cause
 * `classifyGapReason` to tag ALL gaps as `boundary-exhausted`, which the
 * backfill filter excludes — so no fetch ever happens even when the adapter
 * supports `fetchBarsBefore`.
 *
 * This wrapper preserves the original adapter's semantics but corrects
 * `coverageMode` to `anchor-before` and sets `supportsBackwardAnchor: true`
 * when the adapter provides `fetchBarsBefore`.
 */
export function wrapAdapterForBackfill(adapter) {
    const originalSemantics = typeof adapter.getSourceSemantics === "function"
        ? adapter.getSourceSemantics()
        : null;
    const hasFetchBarsBefore = typeof adapter.fetchBarsBefore === "function";
    const wrapped = {
        fetchBarsInRange: adapter.fetchBarsInRange.bind(adapter),
        fetchBarsBefore: adapter.fetchBarsBefore?.bind(adapter),
        subscribeToBar: adapter.subscribeToBar.bind(adapter),
        subscribeToTrades: adapter.subscribeToTrades?.bind(adapter) ?? (() => () => { }),
        subscribeToOrderbook: adapter.subscribeToOrderbook?.bind(adapter) ?? (() => () => { }),
        searchSymbols: adapter.searchSymbols.bind(adapter),
        getSourceSemantics: () => {
            if (!originalSemantics) {
                return {
                    sourceId: "wrapped",
                    sourceContractVersion: "1.0.0",
                    instrumentClass: "custom",
                    coverageMode: "anchor-before",
                    sessionModel: "continuous",
                    timezone: "UTC",
                    supportsExactRangeFetch: false,
                    supportsBackwardAnchor: hasFetchBarsBefore,
                    supportsForwardAnchor: false,
                    supportsLiveBars: false,
                    finalityMode: "immutable",
                    maxBarsPerRequest: 1000,
                    expectedGapReasons: ["not-fetched"],
                };
            }
            const coverageMode = originalSemantics.coverageMode === "finite-snapshot"
                ? "anchor-before"
                : originalSemantics.coverageMode;
            return {
                ...originalSemantics,
                coverageMode,
                supportsBackwardAnchor: hasFetchBarsBefore || originalSemantics.supportsBackwardAnchor,
            };
        },
        getLastBackfillCoverage: adapter.getLastBackfillCoverage?.bind(adapter),
        getLastRangeCoverage: adapter.getLastRangeCoverage?.bind(adapter),
    };
    const sourceAuthHandler = adapter.setAuthHandler;
    if (typeof sourceAuthHandler === "function") {
        wrapped.setAuthHandler = sourceAuthHandler.bind(adapter);
    }
    return wrapped;
}
export const NORMALIZED_DEFAULT_PANES = normalizePaneDescriptorsForExplicitScaleSlots(DEFAULT_PANES);
export function resolveVisiblePanes(panes) {
    const candidatePanes = panes?.length ? normalizePaneDescriptorsForExplicitScaleSlots(panes) : NORMALIZED_DEFAULT_PANES;
    const nextPanes = candidatePanes.filter((pane) => pane.visible);
    return nextPanes.length > 0 ? nextPanes : NORMALIZED_DEFAULT_PANES.filter((pane) => pane.visible);
}
export function inferAxisSideFromScaleSlotId(scaleSlotId) {
    if (scaleSlotId.endsWith("-left")) {
        return "left";
    }
    if (scaleSlotId.endsWith("-right")) {
        return "right";
    }
    return "right";
}
export function mergePaneSnapshotDomains(snapshots, scaleSlotDomains, options) {
    const preferSnapshotPriceDomains = options?.preferSnapshotPriceDomains ?? false;
    return snapshots.map((snapshot) => {
        const scaleSlotIds = new Set([
            ...Object.keys(snapshot.scaleSlotBounds),
            ...Object.keys(snapshot.scaleSlotStates ?? {}),
        ]);
        const scaleSlotStates = Object.fromEntries([...scaleSlotIds].map((scaleSlotId) => {
            const currentState = snapshot.scaleSlotStates[scaleSlotId];
            const shouldPreferComputedDomain = scaleSlotId.startsWith("price-") && !preferSnapshotPriceDomains;
            return [scaleSlotId, {
                    side: currentState?.side ?? inferAxisSideFromScaleSlotId(scaleSlotId),
                    mode: currentState?.mode ?? "auto",
                    visible: currentState?.visible ?? true,
                    domain: shouldPreferComputedDomain
                        ? (scaleSlotDomains[scaleSlotId] ?? currentState?.domain)
                        : (currentState?.domain ?? scaleSlotDomains[scaleSlotId]),
                }];
        }));
        return {
            ...snapshot,
            scaleSlotStates,
        };
    });
}
export function resolvePreferredScaleSlotId(snapshot, requestedScaleSlotId) {
    if (requestedScaleSlotId && snapshot.scaleSlotStates[requestedScaleSlotId]?.visible) {
        return requestedScaleSlotId;
    }
    const visibleScaleSlotIds = Object.entries(snapshot.scaleSlotStates)
        .filter(([, state]) => state.visible)
        .map(([scaleSlotId]) => scaleSlotId);
    if (visibleScaleSlotIds.length === 0) {
        return requestedScaleSlotId && snapshot.scaleSlotStates[requestedScaleSlotId]
            ? requestedScaleSlotId
            : undefined;
    }
    return visibleScaleSlotIds.find((scaleSlotId) => snapshot.scaleSlotStates[scaleSlotId]?.side === "right")
        ?? (requestedScaleSlotId && snapshot.scaleSlotStates[requestedScaleSlotId] ? requestedScaleSlotId : undefined)
        ?? visibleScaleSlotIds[0];
}
export function findPlotDataIndex(point, data) {
    if (point.index >= 0 && point.index < data.length) {
        return point.index;
    }
    return data.findIndex((datum) => datum.date.getTime() === point.date.getTime());
}
export function findPlotDataIndexByTime(timestamp, data) {
    if (data.length === 0) {
        return -1;
    }
    return findNearestDateIndex(data, timestamp);
}
export function resolveForkChartConfigScaleSlotId(snapshot) {
    const visibleRightScaleSlotId = Object.entries(snapshot.scaleSlotStates).find(([, state]) => state.visible && state.side === "right")?.[0];
    if (visibleRightScaleSlotId)
        return visibleRightScaleSlotId;
    const visibleScaleSlotId = Object.entries(snapshot.scaleSlotStates).find(([, state]) => state.visible)?.[0];
    if (visibleScaleSlotId)
        return visibleScaleSlotId;
    return Object.keys(snapshot.scaleSlotStates)[0] ?? null;
}
export { areBoundsEqual, areHeaderAnchorEqual, areLabelAnchorEqual, areAxisBoundsEqual, areBoundsMapEqual, areDomainTupleEqual, areScaleSlotStatesEqual, arePaneSnapshotsEqual, areRuntimeCapabilitiesEqual, } from "./widgetEquality";
